import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult({ mode }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState(mode === "cancel" ? "cancelled" : "polling");
  const attempts = useRef(0);

  useEffect(() => {
    if (mode === "cancel") return;
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (["expired", "failed"].includes(data.status)) {
          setStatus("error");
          return;
        }
      } catch (e) {
        /* retry */
      }
      if (attempts.current >= 10) {
        setStatus("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [mode, params, refresh]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center" data-testid="payment-result">
        {status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-slate-900 animate-spin mx-auto mb-4" />
            <h1 className="font-display text-xl font-bold text-slate-900">Confirming your payment…</h1>
            <p className="text-sm text-slate-500 mt-2">This only takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <h1 className="font-display text-xl font-bold text-slate-900">You're in!</h1>
            <p className="text-sm text-slate-500 mt-2">Your course access is unlocked. Time to start Module 1.</p>
            <Button className="mt-6 w-full bg-slate-900 hover:bg-slate-800" onClick={() => navigate("/dashboard")} data-testid="payment-continue-button">
              Go to dashboard
            </Button>
          </>
        )}
        {(status === "cancelled" || status === "error") && (
          <>
            <XCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h1 className="font-display text-xl font-bold text-slate-900">{status === "cancelled" ? "Checkout cancelled" : "Something went wrong"}</h1>
            <p className="text-sm text-slate-500 mt-2">No charge was made. You can try again whenever you're ready.</p>
            <Button className="mt-6 w-full bg-slate-900 hover:bg-slate-800" onClick={() => navigate("/dashboard")} data-testid="payment-retry-button">
              Back to dashboard
            </Button>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-amber-600 mx-auto mb-4" />
            <h1 className="font-display text-xl font-bold text-slate-900">Payment is processing</h1>
            <p className="text-sm text-slate-500 mt-2">It's taking a little longer than usual. Your access will unlock automatically once confirmed.</p>
            <Button className="mt-6 w-full bg-slate-900 hover:bg-slate-800" onClick={() => navigate("/dashboard")}>
              Go to dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
